/**
 * Hand-expiry formatting + urgency tiers for the card render contract.
 *
 * Urgency thresholds match the T-24h expiry DM (`cardExpiryWarningTask`):
 * amber < 24h, red < 6h. The pill reads these to colour "Expires from hand in
 * 3d 4h". `nowMs` is injected so a `useNowTicker` value drives re-renders
 * without each atom owning a timer.
 */

export type ExpiryUrgency = "calm" | "warn" | "urgent";

const HOUR_MS = 60 * 60 * 1000;

/** amber inside 24h, red inside 6h. */
export const EXPIRY_WARN_MS = 24 * HOUR_MS;
export const EXPIRY_URGENT_MS = 6 * HOUR_MS;

/** Remaining ms until `iso`, or null when missing/unparseable. */
export function msUntil(iso: string | null | undefined, nowMs: number): number | null {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (!Number.isFinite(target)) return null;
  return target - nowMs;
}

/** Urgency tier for a remaining-ms value. Past-due counts as urgent. */
export function urgencyFor(deltaMs: number): ExpiryUrgency {
  if (deltaMs < EXPIRY_URGENT_MS) return "urgent";
  if (deltaMs < EXPIRY_WARN_MS) return "warn";
  return "calm";
}

/**
 * Compact remaining-time label — "3d 4h", "5h 20m", "42m", "30s". Days show
 * days+hours, hours show hours+minutes, sub-hour shows minutes (or seconds
 * under a minute). Returns "0s" for non-positive input.
 */
export function formatRemaining(deltaMs: number): string {
  if (!Number.isFinite(deltaMs) || deltaMs <= 0) return "0s";
  const totalSeconds = Math.floor(deltaMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  if (minutes > 0) return `${minutes}m`;
  return `${seconds}s`;
}

export interface ExpiryReadout {
  /** Remaining ms; negative when already past due. */
  deltaMs: number;
  /** Compact label of the remaining time. */
  label: string;
  urgency: ExpiryUrgency;
  expired: boolean;
}

/**
 * Full readout for a hand-expiry timestamp against `nowMs`. Null when there is
 * no expiry to show (card never auto-expires).
 */
export function expiryReadout(
  iso: string | null | undefined,
  nowMs: number,
): ExpiryReadout | null {
  const deltaMs = msUntil(iso, nowMs);
  if (deltaMs === null) return null;
  return {
    deltaMs,
    label: deltaMs > 0 ? formatRemaining(deltaMs) : "0s",
    urgency: urgencyFor(deltaMs),
    expired: deltaMs <= 0,
  };
}

/** Wall-clock end time for an active effect, e.g. "ends 14:20". null when open-ended. */
export function formatWallClockEnd(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (!Number.isFinite(target)) return null;
  try {
    return new Date(target).toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return null;
  }
}
